<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row">
      <h4 class="col-sm-6" qid="clients-members-page-title">
        {{ client.name }}
        {{ $t('administration.partitions.members.title') }}
      </h4>
      <div class="float-right col-sm-6">
        <div class="row">
          <div class="col-sm-5">
            <div>
              <multiselect
                  v-model="selectedUser"
                  :options="options"
                  :placeholder="$t('administration.partitions.members.select_user_placeholder')"
                  label="name"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  @search-change="asyncFind"
                  qid="clients-members-user-dropdown"
              ></multiselect>
            </div>
          </div>
          <div class="col-sm-5">
            <multiselect
                v-model="selectedRole"
                :options="roles ? roles : []"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('administration.partitions.members.select_role_placeholder')"
                label="label"
                qid="clients-members-role-dropdown"
            ></multiselect>
          </div>
          <div class="col-sm-2">
            <b-button variant="success"
                      qid="create-user-submit-button"
                      class="btn-block"
                      @click="addUser"
                      :disabled="selectedUser && selectedUser.length ? true : false"
            >
              <font-awesome-icon icon="plus"/> {{ $t('administration.partitions.members.add') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <table class="table table-responsive-md">
        <thead>
        <tr>
          <th scope="col">{{ $t('administration.partitions.members.name') }}</th>
          <th scope="col">{{ $t('administration.partitions.members.email') }}</th>
          <th scope="col">{{ $t('administration.partitions.members.phone') }}</th>
          <th scope="col">{{ $t('administration.partitions.members.role') }}</th>
          <th width="100"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="object in tableItems">
          <td>
            <span qid="client-members-name">
              {{object.name}}
            </span>
          </td>
          <td>{{object.email}}</td>
          <td>{{object.phone}}</td>
          <td>
            <span v-for="role in object.userRoles">
              <span v-if="role.client === client.id">
                {{ $t('maintenance.users.role.' + $options.filters.slugify(role.name)) }}
              </span>
            </span>
          </td>
          <td align="right">
            <span class="btn btn-delete" qid="client-members-delete-button" @click="showConfirmDeleteModal(object)">
                <font-awesome-icon icon="trash-alt"/>
            </span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="mt-3 pagination row">
      <div class="col-md-6">
        <div class="row">
          <div class="float-left">
            <span class="text-muted pagination-counter">
              {{ $t('pagination.entries_per_page') }}:
            </span>
          </div>
          <div class="float-left">
            <multiselect
                v-model="perPage"
                :options="perPageOptions"
                track-by="value"
                label="value"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="create-org-form-organization-level"
                @select="perPageChanged"
                :placeholder="selectedPerPage"
                @remove="perPageRemoved"
            ></multiselect>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="selectedPerPage"
            aria-controls="my-table"
            @change="pageChanged"
            limit="1"
        >
          <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
          <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
          <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
          <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
          <div slot="ellipsis-text">
            <b-spinner small type="grow"></b-spinner>
            <b-spinner small type="grow"></b-spinner>
            <b-spinner small type="grow"></b-spinner>
          </div>
          <span class="text-muted" slot="page" slot-scope="{ page, active }">
            <span v-if="active">{{getPerPage}}</span>
          </span>
        </b-pagination>
      </div>
    </div>


    <!-- Remove member from client confirm modal template -->
    <b-modal ref="remove-member" hide-footer>
      <div class="d-block text-center">
        <h4 qid="remove-member-title">
          {{ $t('administration.partitions.members.delete_title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('administration.partitions.members.delete_description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('administration.partitions.members.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="remove-member-cancel-button"
        >
          {{ $t('administration.partitions.members.delete_cancel') }}
        </b-button>
        <b-button @click="confirmRemoveMember(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="remove-member-submit-button"
        >
          {{ $t('administration.partitions.members.delete_submit') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'

  export default {
    name: 'ClientMembers',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.partitions.title'),
            to: '/maintenance/partitions'
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        client: {},
        itemToDelete: {},
        error: '',
        // Needs to be set as an array (because of 3rd party component)
        selectedUser: [],
        selectedRole: [],
        options: [],
        roles: [],
        roleIsInvalid: false,
        userIsInvalid: false
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if (selectedTo > selectedOf) {
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of'), + " " + selectedOf
      }
    },
    methods: {
      loadUsers(page, perPage) {
        this.asyncFind();
        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        piincHttp.get('partitions/' + this.$route.params.slug + '/users', { params: {
            page: pageNumber,
            per_page: perPageNumber,
            client: this.getClient.slug
          }}).then(function(response) {
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {
          // Error
        });
      },
      asyncFind (query) {
        this.isLoading = true
        let self = this;

        piincHttp.get('partitions/' + this.$route.params.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.client = response.data;

          piincHttp.get('users', { params: {
              keyword: query,
              not_member_of_client: self.client.slug,
              client: self.getClient.id
            }}).then(function(response) {
            self.options = response.data.items
            self.isLoading = false
          }, function() {});

        }, function() {});


      },
      addUser() {
        let self = this;
        // Only allow API call if selectedUser and selected roles are not empty arrays
        if (!Array.isArray(this.selectedUser)) {
          piincHttp.post('partitions/' + this.$route.params.slug + '/users', {
            client: this.client.slug,
            user: this.selectedUser.slug,
            role: this.selectedRole? this.selectedRole.id: undefined
          }).then(function(response) {
            // Needs to be set to empty array in order to work
            self.selectedUser = [];
            self.selectedRole = [];
            self.userIsInvalid = false;
            self.roleIsInvalid = false;
            self.$toastr('success', self.$t('administration.partitions.members.add_success'))
            self.loadUsers();
          }, function(error) {})
        } else {
          this.userIsInvalid = true;
          this.roleIsInvalid = true;
        }

      },
      pageChanged(page) {
        this.loadUsers(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadUsers(1, perPage.value)
        self.selectedPerPage = perPage.value
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadUsers(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['remove-member'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {};
        this.$refs['remove-member'].hide()
      },
      confirmRemoveMember(user) {
        let self = this;
        piincHttp.delete('partitions/' + this.$route.params.slug + '/users/' + user.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadUsers(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('administration.partitions.members.delete_success'))
        }, function() {});
      },
    },
    components: {

    },
    created () {
      let self = this;

      piincHttp.get('partitions/' + this.$route.params.slug).then(function(response) {
        self.client = response.data;

        _.forEach(response.data.clientRoles, function(role) {
          if (role.name != 'Client admin'){
            if (role.name == 'Partition admin') {
              role.label = self.$t('maintenance.users.role.partition_admin')
              self.roles.push(role)
            }
            if (role.name == 'Validator') {
              role.label = self.$t('maintenance.users.role.validator')
              self.roles.push(role)
            }
            if (role.name == 'Member') {
              role.label = self.$t('maintenance.users.role.member')
              self.roles.push(role)
            }

          }
        })

        // Push clients name in breadcrumb array
        self.breadcrumbItems.push({text: self.client.name, to: {name: 'maintenance.partitionView', params: {slug: self.client.slug}}});
        self.breadcrumbItems.push({text: self.$t('maintenance.partitions.members.title'), active: true});

      }, function() {});

      this.loadUsers();
    }
  }
</script>
